import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock, Send, Instagram, Facebook, Youtube, Linkedin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/App';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Studio Location',
      content: '123 Photography Lane, Creative District, Mumbai 400001'
    },
    {
      icon: Phone,
      title: 'Phone Number',
      content: '+91 98765 43210'
    },
    {
      icon: Mail,
      title: 'Email Address',
      content: 'hello@niksphotography.com'
    },
    {
      icon: Clock,
      title: 'Working Hours',
      content: 'Mon - Sat: 10 AM - 7 PM'
    }
  ];

  const socialLinks = [
    { icon: Instagram, href: '#', label: 'Instagram' },
    { icon: Facebook, href: '#', label: 'Facebook' },
    { icon: Youtube, href: '#', label: 'YouTube' },
    { icon: Linkedin, href: '#', label: 'LinkedIn' }
  ];

  return (
    <section id="contact" className="py-24 md:py-32 bg-[#0a0a0a]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="text-[#d4af37] text-sm tracking-[0.3em] uppercase font-light mb-4">
            Get in Touch
          </p>
          <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl lg:text-6xl font-medium text-[#e5e5e5]">
            Let's <span className="italic">Connect</span>
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-16">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <div className="space-y-6">
              {contactInfo.map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-start gap-4 group"
                >
                  <div className="p-3 bg-[#1a1a1a] border border-[#262626] group-hover:border-[#d4af37] transition-colors">
                    <item.icon className="w-5 h-5 text-[#d4af37]" />
                  </div>
                  <div>
                    <p className="text-[#a3a3a3] text-sm tracking-wider uppercase mb-1">
                      {item.title}
                    </p>
                    <p className="text-[#e5e5e5]">{item.content}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Social Links */}
            <div className="pt-8 border-t border-[#262626]">
              <p className="text-[#a3a3a3] text-sm tracking-wider uppercase mb-4">
                Follow Us
              </p>
              <div className="flex gap-4">
                {socialLinks.map((social) => (
                  <a
                    key={social.label}
                    href={social.href}
                    className="p-3 bg-[#1a1a1a] border border-[#262626] hover:border-[#d4af37] hover:text-[#d4af37] text-[#a3a3a3] transition-all"
                    aria-label={social.label}
                    data-testid={`social-${social.label.toLowerCase()}`}
                  >
                    <social.icon className="w-5 h-5" />
                  </a>
                ))}
              </div>
            </div>

            {/* Map Placeholder */}
            <div className="mt-8 aspect-video bg-[#1a1a1a] border border-[#262626] overflow-hidden">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d241317.11609823277!2d72.74109995709657!3d19.08219783958221!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7c6306644edc1%3A0x5da4ed8f8d648c69!2sMumbai%2C%20Maharashtra!5e0!3m2!1sen!2sin!4v1673861230000!5m2!1sen!2sin"
                width="100%"
                height="100%"
                style={{ border: 0, filter: 'grayscale(1) invert(0.9) contrast(0.9)' }}
                allowFullScreen=""
                loading="lazy"
                title="Studio Location"
              />
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-[#a3a3a3] text-sm tracking-wider uppercase">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full bg-transparent border-b border-[#262626] py-3 text-[#e5e5e5] focus:border-[#d4af37] focus:outline-none transition-colors"
                    placeholder="John Doe"
                    data-testid="contact-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[#a3a3a3] text-sm tracking-wider uppercase">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full bg-transparent border-b border-[#262626] py-3 text-[#e5e5e5] focus:border-[#d4af37] focus:outline-none transition-colors"
                    placeholder="john@example.com"
                    data-testid="contact-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[#a3a3a3] text-sm tracking-wider uppercase">
                  Subject *
                </label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="w-full bg-transparent border-b border-[#262626] py-3 text-[#e5e5e5] focus:border-[#d4af37] focus:outline-none transition-colors"
                  placeholder="Wedding Photography Inquiry"
                  data-testid="contact-subject-input"
                />
              </div>

              <div className="space-y-2">
                <label className="text-[#a3a3a3] text-sm tracking-wider uppercase">
                  Your Message *
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="w-full bg-transparent border-b border-[#262626] py-3 text-[#e5e5e5] focus:border-[#d4af37] focus:outline-none transition-colors resize-none"
                  placeholder="Tell us about your project..."
                  data-testid="contact-message-input"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full md:w-auto bg-[#d4af37] text-[#0a0a0a] hover:bg-[#b3922d] rounded-none px-12 py-6 tracking-widest uppercase text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="contact-submit-btn"
              >
                {isSubmitting ? (
                  'Sending...'
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Message
                  </>
                )}
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
